import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { forwardTo } from 'utils/common/route';

import { CustomNotification } from 'common/components';

import { saveQaSpecDataPackaging } from 'services/product';
import { qaFreshProduceKeys } from '../qa-fresh-produce/hooks';
import { getKeyProductSpec } from 'pages/qa-spec/hooks/useProductSpecInfo';
import { getKeyProductIngredients } from 'pages/product-full-view/components/product-media-area/multiple-panel/tab-ingredients/useProductIngredients';
import { productFullViewKeys } from 'hooks/product-full/hooks';
import { qaAllergensKeys } from '../qa-allergen/hooks';
import { groupProductData } from 'pages/product-full-view/components/product-media-area/shared/hooks';
import { queryKeyQaSpecIngredients } from 'pages/qa-spec/components/qa-ingredient/hook';
import { keyGetProductItemModules } from 'pages/product-full-view/shared/hooks';
import * as productServices from 'services/product';
import { getQaClaimsTabDataQueryKey } from 'pages/qa-spec/components/qa-claims/queries';
import {
  transformFormValuesQaSpecToParams,
  transformInitialDataToFormValues,
} from 'pages/qa-spec/utils/transformQaBooleanValue';

export const getQaPackagingTabDataQueryKey = (productId) => {
  return ['qa-spec', parseInt(productId), 'qa-spec-data-packing'];
};

const LABELING_FORMAT_KEYS = [
  // 'frontOfPackIcons',
  // 'recyclable',
  // 'bilingual', // maybe need to be supported from BE
  // 'productContainsBioengineeredIngredients',
  'qrCode',
  'hasFoodGradePlasticsCertifier',
  'recycledMaterial',
  'soyInk',
  'vegetableBasedInk',
];

export const useGetQaPackagingTabData = ({ productId }, options) => {
  const queries = useQuery({
    queryKey: getQaPackagingTabDataQueryKey(productId),
    queryFn: async () => {
      const response = await productServices.getQaPacking({
        productId,
      });
      if (response?.isSuccess) {
        return response?.data;
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong!');
        forwardTo('/products');
      }
    },
    ...options,
  });

  return {
    ...queries,
    data: useMemo(
      () =>
        transformInitialDataToFormValues(queries?.data, LABELING_FORMAT_KEYS),
      [queries?.data]
    ),
  };
};

export const useSaveQaPackagingTab = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, formData }) => {
      const mappingFormData = transformFormValuesQaSpecToParams(
        formData,
        LABELING_FORMAT_KEYS
      );

      const params = { productId, ...mappingFormData };

      const response = await saveQaSpecDataPackaging(params);

      const { isSuccess, data, message } = response || {};

      if (isSuccess) {
        return Promise.resolve(data);
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
    onSuccess: async (data, { productId }) => {
      queryClient.invalidateQueries({
        queryKey: getQaPackagingTabDataQueryKey(productId),
      });
      queryClient.invalidateQueries({
        queryKey: qaFreshProduceKeys.detail(productId),
      });
      queryClient.invalidateQueries({
        queryKey: getKeyProductSpec(productId),
      });
      queryClient.invalidateQueries({
        queryKey: getKeyProductIngredients(productId),
      });
      queryClient.invalidateQueries({
        queryKey: productFullViewKeys.itemData(productId),
      });
      queryClient.invalidateQueries({
        queryKey: qaAllergensKeys.list(productId),
      });
      queryClient.invalidateQueries({
        queryKey: groupProductData.getAllGroups(productId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeyQaSpecIngredients(productId),
      });
      // ADVANCED MODULES
      queryClient.invalidateQueries({
        queryKey: keyGetProductItemModules(productId),
      });
      //* sync to qa claim
      queryClient.invalidateQueries({
        queryKey: getQaClaimsTabDataQueryKey(productId),
      });

      CustomNotification.success('Update Labeling data successfully!');
    },
    onError: (error) => {
      CustomNotification.error(error || 'Server Error');
    },
  });
};
